import { supabase } from '@/lib/supabase/client';
import { Comment, UpdateCommentParams } from '../types/comment';

interface PreparedResponse {
  id: string;
  content: string;
  category: string;
}

/**
 * Récupère les commentaires pour plusieurs profils sociaux
 */
export const loadProfileComments = async (profileIds: string[]): Promise<Comment[]> => {
  try {
    if (profileIds.length === 0) return [];
    
    const { data, error } = await supabase
      .from('comments')
      .select('*')
      .in('profile_id', profileIds)
      .order('created_at', { ascending: false });
    
    if (error) {
      console.error('Erreur lors de la récupération des commentaires:', error);
      throw error;
    }
    
    return data.map(comment => ({
      id: comment.id,
      profileId: comment.profile_id,
      postId: comment.post_id || '',
      author: {
        name: comment.author_name || 'Anonyme',
        username: comment.author_username || 'anonyme',
        avatar: comment.author_avatar || '',
      },
      content: comment.content || '',
      timestamp: new Date(comment.created_at),
      isRead: comment.is_read || false,
      platform: comment.platform,
      status: comment.sentiment || 'neutral',
      sentimentLabel: comment.sentiment_label || '',
      videoTitle: comment.video_title || '',
      videoId: comment.video_id || '',
      likes: comment.likes_count || 0,
      dislikes: comment.dislikes_count || 0,
      aiGeneratedReply: comment.ai_generated_reply,
      replyStatus: comment.reply_status || 'not_replied',
    }));
  } catch (error) {
    console.error('Exception lors de la récupération des commentaires:', error);
    return [];
  }
};

/**
 * Récupère les commentaires pour un profil social spécifique
 */
export const fetchComments = async (profileId: string): Promise<Comment[]> => {
  try {
    const { data, error } = await supabase
      .from('comments')
      .select('*')
      .eq('profile_id', profileId)
      .order('created_at', { ascending: false });
    
    if (error) {
      console.error('Erreur lors de la récupération des commentaires:', error);
      throw error;
    }
    
    return data.map(comment => ({
      id: comment.id,
      profileId: comment.profile_id,
      postId: comment.post_id || '',
      author: {
        name: comment.author_name || 'Anonyme',
        username: comment.author_username || 'anonyme',
        avatar: comment.author_avatar || '',
      },
      content: comment.content || '',
      timestamp: new Date(comment.created_at),
      isRead: comment.is_read || false,
      platform: comment.platform,
      status: comment.sentiment || 'neutral',
      videoTitle: comment.video_title || '',
      videoId: comment.video_id || '',
      likes: comment.likes_count || 0,
      dislikes: comment.dislikes_count || 0,
      aiGeneratedReply: comment.ai_generated_reply,
      replyStatus: comment.reply_status || 'not_replied',
    }));
  } catch (error) {
    console.error('Exception lors de la récupération des commentaires:', error);
    return [];
  }
};

/**
 * Met à jour le statut de lecture d'un commentaire
 */
export const updateCommentReadStatus = async (commentId: string | UpdateCommentParams): Promise<boolean> => {
  try {
    const params = typeof commentId === 'string' 
      ? { commentId, isRead: true } 
      : commentId;
    
    const { error } = await supabase
      .from('comments')
      .update({ is_read: params.isRead })
      .eq('id', params.commentId);
    
    if (error) {
      console.error('Erreur lors de la mise à jour du statut de lecture:', error);
      return false;
    }
    
    return true;
  } catch (error) {
    console.error('Exception lors de la mise à jour du statut de lecture:', error);
    return false;
  }
};

/**
 * Récupère les réponses préparées depuis la base de données
 */
export const fetchPreparedResponses = async (): Promise<PreparedResponse[]> => {
  try {
    // Dans une version future, ces réponses pourraient venir de la base de données
    // Pour l'instant, on retourne des réponses statiques
    const mockResponses: PreparedResponse[] = [
      { id: '1', content: 'Merci pour votre commentaire ! Nous apprécions votre feedback.', category: 'Remerciement' },
      { id: '2', content: 'Nous sommes désolés pour cette expérience. Pouvez-vous nous fournir plus de détails ?', category: 'Service client' },
      { id: '3', content: 'Nous avons bien pris note de votre demande et allons la traiter rapidement.', category: 'Service client' },
      { id: '4', content: 'Nous sommes ravis que notre produit vous plaise ! N\'hésitez pas à partager votre expérience.', category: 'Positif' }
    ];
    
    return mockResponses;
  } catch (error) {
    console.error('Erreur lors de la récupération des réponses préparées:', error);
    return [];
  }
};

/**
 * Envoie une réponse à un commentaire
 */
export const sendReply = async (commentId: string, replyContent: string): Promise<boolean> => {
  try {
    // Simulation d'envoi de réponse
    console.log(`Envoi de réponse au commentaire ${commentId}: ${replyContent}`);
    
    // En production, on ferait un appel à l'API appropriée ici
    // Pour l'instant, on simule un délai et on retourne un succès
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Mise à jour du statut de réponse dans la base de données
    const { error } = await supabase
      .from('comments')
      .update({ 
        reply_status: 'published',
        reply_content: replyContent,
        replied_at: new Date().toISOString()
      })
      .eq('id', commentId);
    
    if (error) {
      console.error('Erreur lors de la mise à jour du statut de réponse:', error);
      return false;
    }
    
    return true;
  } catch (error) {
    console.error('Exception lors de l\'envoi de la réponse:', error);
    return false;
  }
};
